import { Box, useTheme } from "@mui/material";
import { ExternalLink, Pencil, Percent, Unlink } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { SolutionItemActionRow } from "web_myplann/Goals/Components/SolutionItemActionRow";
import {
	type GoalPortfolioResponseDTO,
	GoalPortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

// DEV-5334: hidden until the conference. Saving a contract valued by the producer fails (and a locked one after DEV-3229). Show again once DEV-3926 and DEV-3229 are done.
const SHOW_UPDATE_VALUE = false;

type Props = {
	open: boolean;
	portfolio: GoalPortfolioResponseDTO;
	onClose: () => void;
	onRequestUnpin: () => void;
	onUpdateValue: () => void;
	onEditAllocation?: (portfolio: GoalPortfolioResponseDTO) => void;
};

export const SolutionItemActionDrawer: FC<Props> = ({
	open,
	portfolio,
	onClose,
	onRequestUnpin,
	onUpdateValue,
	onEditAllocation,
}) => {
	const { t } = useResource();
	const navigate = useNavigate();
	const theme = useTheme();

	const handleDetail = () => {
		navigate(
			Routing.Portfolio.PortfolioDetail.path(portfolio.portfolioId.toString()),
		);
		onClose();
	};

	const handleUpdateValue = () => {
		onClose();
		onUpdateValue();
	};

	const handleEditAllocation = () => {
		onEditAllocation?.(portfolio);
		onClose();
	};

	const handleUnpin = () => {
		onClose();
		onRequestUnpin();
	};

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={portfolio.name ?? ""}
			closeButton
		>
			<Box pb={1}>
				<SolutionItemActionRow
					onClick={handleDetail}
					Icon={ExternalLink}
					iconBg={theme.palette.info.light}
					iconColor={theme.palette.info.main}
					label={t(Resources.Goals.Detail.Solution.Actions.detail)}
					hasDivider
				/>
				{SHOW_UPDATE_VALUE && (
					<SolutionItemActionRow
						onClick={handleUpdateValue}
						Icon={Pencil}
						iconBg={theme.palette.success.light}
						iconColor={theme.palette.success.main}
						label={t(Resources.Goals.Detail.Solution.Actions.updateValue)}
						hasDivider
					/>
				)}
				{portfolio.type !== GoalPortfolioTypeEnum.SetAside && (
					<SolutionItemActionRow
						onClick={handleEditAllocation}
						Icon={Percent}
						iconBg={theme.palette.purple.light}
						iconColor={theme.palette.purple.main}
						label={t(Resources.Goals.Detail.Solution.Actions.editAllocation)}
						description={t(
							Resources.Goals.Detail.Solution.Actions.editAllocationDescription,
							{ percent: portfolio.usagePercent },
						)}
						hasDivider
					/>
				)}
				<SolutionItemActionRow
					onClick={handleUnpin}
					Icon={Unlink}
					iconBg={theme.palette.error.light}
					iconColor={theme.palette.error.main}
					label={t(Resources.Goals.Detail.Solution.Actions.unpin)}
				/>
			</Box>
		</MyPlannDrawer>
	);
};
